import React from 'react';
import { Box, Chip, CircularProgress, Typography } from '@mui/material';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import BlockIcon from '@mui/icons-material/Block';
import { AgentState, Command, RunRow, STATUS_LOOK } from './types';

// Snowflake TIMESTAMP_NTZ values arrive as 'YYYY-MM-DD HH:MM:SS.fff' in UTC; the hub's own times are ISO.
export const fmtTime = (iso: string | null | undefined) => (iso ? new Date(iso.endsWith('Z') || iso.includes('+') ? iso : `${iso.replace(' ', 'T')}Z`).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—');

/** "just now", "12 min ago", "3 h ago", "2 days ago"; the exact time goes in a tooltip (fmtTime). */
export function fmtAgo(iso: string | null | undefined): string {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso.endsWith('Z') || iso.includes('+') ? iso : `${iso.replace(' ', 'T')}Z`).getTime();
  const min = Math.round(ms / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.round(h / 24);
  return `${d} day${d === 1 ? '' : 's'} ago`;
}

export function scheduleLabel(s: string): string {
  if (s === 'on demand') return 'On demand';
  if (s === 'continuous') return 'Whenever there is room';
  if (s.startsWith('every')) return s.replace(/^every/, 'Every');
  const m = /^(\d+) (\d+) \* \* \*$/.exec(s);
  if (m) return `Daily ${new Date(2026, 0, 1, Number(m[2]), Number(m[1])).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })} PT`;
  return `cron ${s}`;
}

/**
 * A run's outcome in one plain line: what it was about (the PR, or its task), then what it found. Agents
 * often end with "Done" or markdown, so those are cleaned off.
 */
export function runLine(r: { summary: string | null; error: string | null; status: string; task?: string | null; subject?: string | null }): string {
  if (r.status === 'error' && r.error) return r.error;
  const said = (r.summary ?? '').replace(/\[([^\]]+)\]\([^)]+\)/g, '$1').replace(/^done[\s:.—-]*/i, '').trim();
  const about = r.subject ?? (r.task ? r.task.split('\n')[0].slice(0, 80) : '');
  return [about, said].filter(Boolean).join(' · ') || (r.status === 'done' ? 'Finished' : '—');
}

export function StatusChip({ status }: { status: RunRow['status'] }) {
  const s = STATUS_LOOK[status] ?? { label: status, fg: '#475569', bg: '#F1F5F9' };
  return <Chip size="small" label={s.label} sx={{ height: 20, fontSize: 11, fontWeight: 600, color: s.fg, bgcolor: s.bg }} />;
}

/** "Noonlash#4" → its GitHub pull request; null for a run that wasn't about a PR. */
export function prUrl(subject: string | null | undefined): string | null {
  const m = /^([\w.-]+)#(\d+)$/.exec(subject ?? '');
  return m ? `https://github.com/shareably/${m[1]}/pull/${m[2]}` : null;
}

/** A pause / resume / run-now the page sent and the hub hasn't answered yet (a second or two). */
export interface PendingCommand { command: Command; at: number }

const PENDING_LABEL: Partial<Record<Command, string>> = { pause: 'Pausing…', resume: 'Resuming…', run_now: 'Starting…' };

/** Whether an agent will run: active, paused, running now, draft or retired, with an icon. */
export function AgentStatus({ a, pending }: { a: AgentState; pending?: PendingCommand }) {
  const look = (icon: React.ReactNode, label: string, color: string) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color, whiteSpace: 'nowrap' }}>
      {icon}<Typography variant="body2" sx={{ fontWeight: 600, color: 'inherit' }}>{label}</Typography>
    </Box>
  );
  if (pending) return look(<CircularProgress aria-hidden size={14} thickness={5} sx={{ color: 'inherit' }} />, PENDING_LABEL[pending.command] ?? 'Sending…', '#64748B');
  if (a.status === 'retired') return look(<BlockIcon sx={{ fontSize: 18 }} />, 'Retired', '#94A3B8');
  if (a.running) return look(<CircularProgress aria-hidden size={14} thickness={5} sx={{ color: 'inherit' }} />, 'Running', '#2563EB');
  if (a.paused) return look(<PauseCircleOutlineIcon sx={{ fontSize: 18 }} />, 'Paused', '#B45309');
  if (a.status === 'draft') return look(<EditOutlinedIcon sx={{ fontSize: 18 }} />, 'Draft', '#7E22CE');
  return look(<CheckCircleOutlineIcon sx={{ fontSize: 18 }} />, 'Active', '#15803D');
}

/** When an agent runs, in plain words: its schedule, each pull request (the PR pipeline), or when given a task. */
export function whenItRuns(a: AgentState): string {
  if (a.settings?.workdir === 'pr') return 'On each pull request';
  if (a.schedule !== 'on demand') return `${scheduleLabel(a.schedule)}${a.onDemand ? ', and when given a task' : ''}`;
  return a.onDemand ? 'When given a task' : 'Only when another agent asks';
}
